import { useMemo, useCallback } from 'react';
import sortBy from 'lodash/sortBy';
import pluralize from 'pluralize';

import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import {
  SettingsGroup,
  SettingsLabel,
  SettingSmallText,
} from '../settings/settings.style';
import { Student } from '../classroom/classroom.constants';
import { DownloadAnswerFormat } from './teacher-download-student-answer.constants';
import { AnchorLinkErrorStyled } from 'approot/shared/styles/typography.style';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import { PRIVATE_MY_STUDENTS } from 'global/private-routes.constants';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { DropdownListItem } from 'webclient.constants';
import DropdownMultiselectAllToggle from 'approot/shared/dropdown/dropdown-multiselect-all-toggle';
import { EVENT_TARGET_TYPE_CTA } from 'approot/shared/data-tracker/data-events.constants';
import { addStudentsPushDataTrackingEvent } from 'approot/teacher-student/logic/add-students-push-data-tracking-event';

type Props = {
  format: string;
  selectedStudents?: Student[];
  allStudents: Student[];
  noSelectedStudents: boolean;
  noStudentsWithAnswers: boolean;
  isDownloading: boolean;
  setSelectedStudents: (students: Student[]) => void;
  setFormat: (format: DownloadAnswerFormat) => void;
  studentsWithAnswers: Student[];
  studentsWithoutAnswers: Set<number>;
};

const TeacherDownloadStudentAnswersOptions = ({
  format,
  selectedStudents,
  allStudents,
  noSelectedStudents,
  noStudentsWithAnswers,
  isDownloading,
  setFormat,
  setSelectedStudents,
  studentsWithAnswers,
  studentsWithoutAnswers,
}: Props) => {
  const studentOptions: DropdownListItem[] = useMemo(() => {
    const sortedStudents = sortBy(
      studentsWithAnswers.map(student => ({
        value: student.id.toString(),
        label: getStudentDisplayName(student),
      })),
      s => s.label.toLowerCase()
    );

    return sortedStudents;
  }, [studentsWithAnswers]);

  const getSelectedStudentIds = useCallback(() => {
    return (selectedStudents || []).map(s => s.id.toString());
  }, [selectedStudents]);

  const handleStudentSelect = useCallback(
    (values: string[]) => {
      if (!setSelectedStudents) return;

      const selectedStudentObjs = studentsWithAnswers.filter(student =>
        values.includes(student.id.toString())
      );

      setSelectedStudents(selectedStudentObjs);
    },
    [setSelectedStudents, studentsWithAnswers]
  );

  return (
    <>
      <SettingsGroup>
        <SettingsLabel>Format</SettingsLabel>
        <CheckableSingle
          name="format"
          options={[
            {
              label: 'PDF',
              value: DownloadAnswerFormat.Pdf,
              isDisabled: isDownloading,
            },
            {
              label: 'Spreadsheet',
              value: DownloadAnswerFormat.Spreadsheet,
              isDisabled: isDownloading,
            },
          ]}
          selected={format}
          onSelect={value => {
            setFormat(value as DownloadAnswerFormat);
            setSelectedStudents(allStudents);
          }}
        />
        <SettingSmallText marginTop={8}>
          {format === DownloadAnswerFormat.Pdf
            ? 'Recommended for printing.'
            : 'Recommended for data collection.'}
        </SettingSmallText>
      </SettingsGroup>
      <SettingsGroup>
        <SettingsLabel>
          {format === DownloadAnswerFormat.Pdf
            ? 'Students with answers'
            : 'Students'}
        </SettingsLabel>
        <DropdownMultiselectAllToggle
          label={`${getSelectedStudentIds().length} ${pluralize(
            'student',
            getSelectedStudentIds().length
          )}`}
          options={studentOptions}
          dataSelector="student-answers"
          selectedOptions={getSelectedStudentIds()}
          handleChangeCheckbox={handleStudentSelect}
          disabled={
            format === DownloadAnswerFormat.Spreadsheet ||
            isDownloading ||
            noStudentsWithAnswers
          }
        />

        {noStudentsWithAnswers ? (
          <ErrorMessageStyled>
            No student responses are available yet. Students may not have
            started answering, or you may need to{' '}
            <AnchorLinkErrorStyled
              to={getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))}
              aria-label="Add students. Opens in a new tab."
              target="_blank"
              data-testid="btn-add-students"
              onClick={() => {
                addStudentsPushDataTrackingEvent({
                  targetType: EVENT_TARGET_TYPE_CTA,
                  targetIdentifier: 'download-student-answers',
                });
              }}
            >
              <strong>add students</strong>
            </AnchorLinkErrorStyled>{' '}
            to this class to collect and download responses.
          </ErrorMessageStyled>
        ) : (
          <SettingSmallText marginTop={8}>
            {format === DownloadAnswerFormat.Pdf
              ? `Only students who have submitted a response appear here. ${studentsWithoutAnswers.size} students haven’t responded yet.`
              : null}
          </SettingSmallText>
        )}
        {noSelectedStudents && !noStudentsWithAnswers && (
          <ErrorMessageStyled>
            {format === DownloadAnswerFormat.Pdf
              ? 'Select at least one student to download their responses.'
              : 'Select a student to download their responses.'}
          </ErrorMessageStyled>
        )}
      </SettingsGroup>
    </>
  );
};

export default TeacherDownloadStudentAnswersOptions;
